import { Children, createContext, Fragment, isValidElement, useContext, useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { CircleHelp, X } from 'lucide-react'
import { cn } from '@/lib/utils'

/* ERP · modelo "lista e detalhe" das telas da Sponsor (pedido do usuário em 27/10: telas sobrecarregadas de texto).
   A tela mostra só o que decide "quem eu atendo agora"; o resto aparece no painel ao lado, ao clicar.
   Regras: cabeçalho com no máximo 2 linhas (o número do dia + até 2 números de apoio; sem o caminho "Sponsor · …", a
   árvore do menu já diz onde a pessoa está); uma linha por item com no máximo 3 informações; a cor diz a urgência; os
   detalhes e as ações ficam no painel; a explicação vira o (?) do título.

   DayHeader({ title, facts?, help?, actions? })
     title: o número do dia numa frase curta ("10 esperando boas-vindas"); facts: até 2 { value, label } numa linha
     apagada (o resto é cortado); help: a explicação da tela, aberta pelo (?) ao lado do título; actions: à direita.
   ListDetail({ list, detail, label? })  — a lista na largura toda; o detalhe (DetailPanel) abre numa janela no meio
     da tela (pedido do usuário em 27/10, no lugar do painel ao lado). detail null = só a lista.
   LineRow({ tone?, toneLabel?, title, cells?, selected?, onSelect })  — uma linha (~40px), clicável inteira: bolinha da urgência
     (toneLabel = o que a cor quer dizer, para leitor de tela),
     o nome e até 3 células curtas à direita (alinhadas, números tabulares). Sem botão "Abrir".
   LineList({ label, children, empty? })  — painel branco com as LineRow.
   DetailPanel({ title, sub?, facts?, actions?, children?, onClose })  — o detalhe numa janela no meio da tela, com o
     fundo escurecido: título, uma linha apagada, fatos { label, value } em duas colunas, as ações e o que mais a tela
     quiser (children). Fecha com Esc, com o X ou clicando fora.
   DetailStats({ items })  — até 3 números grandes no topo do detalhe ({ value, label, tone? }), no lugar de uma frase
     com vários números ("11 no mês · 8 abertas · 3 atrasadas").
   MiniBar({ value, label? })  — barra horizontal de 0 a 1 com o rótulo ao lado (ex.: "73%"), para tabelas de ranking. */

export type LineTone = 'err' | 'warn' | 'ok' | 'info' | 'neutral'

const DOT: Record<LineTone, string> = {
    err: 'var(--red)',
    warn: 'var(--yellow)',
    ok: 'var(--green)',
    info: 'var(--info)',
    neutral: 'var(--line-2, var(--line))',
}

export type HeaderFact = { value: ReactNode; label: string }

/** No máximo 2 números de apoio no cabeçalho (o resto é cortado de propósito). */
export const HEADER_FACTS_MAX = 2

export function DayHeader({ title, facts, help, actions }: { title: ReactNode; facts?: readonly HeaderFact[]; help?: ReactNode; actions?: ReactNode }) {
    const items = (facts ?? []).slice(0, HEADER_FACTS_MAX)
    const titleId = useId()
    return (
        <section aria-labelledby={titleId} className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
            <div className="flex min-w-0 flex-[1_1_360px] flex-col gap-0.5">
                <div className="flex items-center gap-1.5">
                    <h1 id={titleId} className="m-0 text-[20px] leading-[1.25] font-semibold tracking-[-0.01em] text-balance">{title}</h1>
                    {help && <HelpButton>{help}</HelpButton>}
                </div>
                {items.length > 0 && (
                    <p className="m-0 flex flex-wrap gap-x-1.5 text-[13px] text-muted">
                        {items.map((f, i) => (
                            <span key={f.label}>
                                {i > 0 && <span aria-hidden="true" className="mr-1.5">·</span>}
                                <b className="tabular font-semibold text-fg">{f.value}</b> {f.label}
                            </span>
                        ))}
                    </p>
                )}
            </div>
            {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </section>
    )
}

/** O (?) do título: a explicação da tela num balão pequeno (Esc e clique fora fecham). */
export function HelpButton({ children }: { children: ReactNode }) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLSpanElement>(null)
    const id = useId()
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
        const onDown = (e: PointerEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
        document.addEventListener('keydown', onKey)
        document.addEventListener('pointerdown', onDown)
        return () => {
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('pointerdown', onDown)
        }
    }, [open])
    return (
        <span ref={ref} className="relative inline-flex">
            <button
                type="button"
                aria-label="Como funciona"
                aria-expanded={open}
                aria-controls={id}
                onClick={() => setOpen(v => !v)}
                className="grid size-7 cursor-pointer place-items-center rounded-full border-0 bg-transparent text-muted hover:bg-[var(--hover)] hover:text-fg"
            >
                <CircleHelp size={16} aria-hidden="true" />
            </button>
            {open && (
                <div
                    id={id}
                    role="dialog"
                    aria-label="Como funciona"
                    className="absolute top-full left-0 z-40 mt-1 flex w-[min(360px,80vw)] flex-col gap-1.5 rounded-[12px] border border-line bg-panel p-3 text-[13px] leading-[1.5] text-muted"
                    style={{ boxShadow: '0 12px 32px var(--shadow), 0 2px 6px var(--shadow)' }}
                >
                    {children}
                </div>
            )}
        </span>
    )
}

export function ListDetail({ list, detail, label = 'Detalhe' }: { list: ReactNode; detail: ReactNode | null; label?: string }) {
    return (
        <div className="min-w-0">
            {list}
            {detail && <DetailLabel.Provider value={label}>{detail}</DetailLabel.Provider>}
        </div>
    )
}

export function LineRow({ tone, toneLabel, title, cells, selected = false, onSelect }: { tone?: LineTone; toneLabel?: string; title: ReactNode; cells?: readonly ReactNode[]; selected?: boolean; onSelect: () => void }) {
    return (
        <li className="border-t border-line first:border-t-0">
            <button
                type="button"
                aria-pressed={selected}
                onClick={onSelect}
                className={cn('flex min-h-10 w-full cursor-pointer items-center gap-3 rounded-[8px] border-0 px-2 py-1 text-left', selected ? 'bg-tint' : 'bg-transparent hover:bg-[var(--hover)]')}
            >
                {tone && <span aria-hidden="true" className="size-2 flex-none rounded-full" style={{ background: DOT[tone] }} />}
                {toneLabel && <span className="sr-only">{toneLabel}: </span>}
                <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-fg">{title}</span>
                {(cells ?? []).slice(0, 3).map((c, i) => (
                    // No celular só a 1ª célula; as outras ficam no painel
                    <span key={i} className={cn('tabular flex-none text-right text-[13px] text-muted min-[560px]:min-w-[72px]', i > 0 && 'hidden min-[560px]:inline')}>{c}</span>
                ))}
            </button>
        </li>
    )
}

export function LineList({ label, children, empty }: { label: string; children?: ReactNode; empty?: ReactNode }) {
    const hasRows = Array.isArray(children) ? children.some(Boolean) : !!children
    return (
        <div className="min-w-0 rounded-[14px] border border-line bg-panel px-1.5 py-1">
            {hasRows ? (
                <ul aria-label={label} className="m-0 flex list-none flex-col p-0">{children}</ul>
            ) : (
                <p className="m-0 px-2 py-3 text-[13px] text-muted">{empty ?? 'Nada por aqui.'}</p>
            )}
        </div>
    )
}

export type DetailFact = { label: string; value: ReactNode }

const DetailLabel = createContext('Detalhe')

/** Os números grandes (DetailStats) sobem para o topo do detalhe, antes dos fatos, mesmo passados como filhos. */
function splitStats(children: ReactNode): { stats: ReactNode[]; rest: ReactNode[] } {
    const stats: ReactNode[] = []
    const rest: ReactNode[] = []
    const walk = (nodes: ReactNode) => Children.forEach(nodes, n => {
        if (isValidElement(n) && n.type === Fragment) walk((n as ReactElement<{ children?: ReactNode }>).props.children)
        else if (isValidElement(n) && n.type === DetailStats) stats.push(n)
        else rest.push(n)
    })
    walk(children)
    return { stats, rest }
}

export function DetailPanel({ title, sub, facts, actions, children, onClose }: { title: ReactNode; sub?: ReactNode; facts?: readonly DetailFact[]; actions?: ReactNode; children?: ReactNode; onClose: () => void }) {
    const label = useContext(DetailLabel)
    const { stats, rest } = splitStats(children)
    const box = useRef<HTMLDivElement>(null)
    const close = useRef(onClose)
    close.current = onClose
    useEffect(() => {
        const back = document.activeElement as HTMLElement | null
        box.current?.focus()
        // Esc: só o de cima (um balão ou seletor aberto dentro do detalhe fecha primeiro e marca o evento)
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) close.current() }
        document.addEventListener('keydown', onKey)
        const overflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = overflow
            back?.focus?.()
        }
    }, [])
    return createPortal(
        <div
            className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/30 p-4 max-[560px]:items-end max-[560px]:p-0"
            onPointerDown={e => { if (e.target === e.currentTarget) onClose() }}
        >
            <div
                ref={box}
                role="dialog"
                aria-modal="true"
                aria-label={label}
                tabIndex={-1}
                className="my-auto flex w-full max-w-[600px] flex-col gap-3 rounded-[16px] border border-line bg-panel p-5 outline-none max-[560px]:max-h-[90dvh] max-[560px]:mb-0 max-[560px]:overflow-y-auto max-[560px]:rounded-b-none"
                style={{ boxShadow: '0 24px 64px var(--shadow), 0 4px 12px var(--shadow)' }}
            >
                <div className="flex items-start gap-2">
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <h2 className="m-0 truncate text-[17px] leading-[1.3] font-semibold">{title}</h2>
                        {sub && <p className="m-0 text-[13px] text-muted">{sub}</p>}
                    </div>
                    <button type="button" aria-label="Fechar" onClick={onClose} className="grid size-8 flex-none cursor-pointer place-items-center rounded-[8px] border-0 bg-transparent text-muted hover:bg-[var(--hover)]">
                        <X size={16} aria-hidden="true" />
                    </button>
                </div>
                {stats}
                {facts && facts.length > 0 && (
                    <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-2.5 min-[560px]:grid-cols-2">
                        {facts.map(f => (
                            <div key={f.label} className="flex flex-col gap-0.5">
                                <dt className="text-[12px] text-muted">{f.label}</dt>
                                <dd className="m-0 text-[13.5px] text-fg">{f.value}</dd>
                            </div>
                        ))}
                    </dl>
                )}
                {rest}
                {actions && <div className="flex flex-wrap gap-2 border-t border-line pt-3">{actions}</div>}
            </div>
        </div>,
        document.body,
    )
}

export function MiniBar({ value, label }: { value: number | null; label?: ReactNode }) {
    const v = value === null || !Number.isFinite(value) ? null : Math.max(0, Math.min(1, value))
    return (
        <span className="inline-flex items-center gap-2">
            <span aria-hidden="true" className="h-1.5 w-[72px] overflow-hidden rounded-full bg-[var(--well)]">
                {v !== null && <span className="block h-full rounded-full bg-green" style={{ width: `${v * 100}%` }} />}
            </span>
            <span className="tabular min-w-[36px] text-right text-[13px] text-fg">{label ?? (v === null ? '—' : `${Math.round(v * 100)}%`)}</span>
        </span>
    )
}

export type DetailStat = { value: ReactNode; label: string; tone?: 'err' | 'warn' | 'ok' }

const STAT_INK: Record<NonNullable<DetailStat['tone']>, string> = { err: 'var(--red-ink)', warn: 'var(--yellow-ink)', ok: 'var(--ok-ink)' }

export function DetailStats({ items }: { items: readonly DetailStat[] }) {
    return (
        <dl className="m-0 grid grid-cols-3 gap-2">
            {items.slice(0, 3).map(s => (
                <div key={s.label} className="flex flex-col gap-0.5 rounded-[12px] bg-[var(--well)] px-3 py-2">
                    <dd className="tabular m-0 text-[20px] leading-[1.2] font-semibold" style={{ color: s.tone ? STAT_INK[s.tone] : 'var(--text)' }}>{s.value}</dd>
                    <dt className="order-first text-[12px] text-muted">{s.label}</dt>
                </div>
            ))}
        </dl>
    )
}
